import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router'

/** Current sub-view of an overview-style service module (absence / time tracking). */
export type ModuleSubView =
  | { mode: 'overview' }
  | { mode: 'all' }
  | { mode: 'employee'; employeeId: string }

/**
 * Drives the sub-view (overview / all entries / single employee) of the absence and
 * time-tracking module pages through the URL — mirroring the `?open`/`?folder` scheme
 * of `ServiceModulesPage`. The sub-view lives in the `view` (+ `employeeId`) search
 * params so each view is its own URL: shareable, and browser back/forward heals itself.
 * `open` (the module id) is always preserved so the module page stays mounted.
 */
export function useModuleSubView() {
  const [searchParams, setSearchParams] = useSearchParams()

  const view = useMemo<ModuleSubView>(() => {
    const mode = searchParams.get('view')
    if (mode === 'all') return { mode: 'all' }
    if (mode === 'employee') {
      const employeeId = searchParams.get('employeeId')
      if (employeeId) return { mode: 'employee', employeeId }
    }
    return { mode: 'overview' }
  }, [searchParams])

  // Push a new history entry (default) so back/forward walks the sub-views; only the
  // sub-view params are touched, `open` and everything else carry over unchanged.
  const setView = useCallback(
    (next: ModuleSubView) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev)
        params.delete('view')
        params.delete('employeeId')
        if (next.mode === 'all') params.set('view', 'all')
        if (next.mode === 'employee') {
          params.set('view', 'employee')
          params.set('employeeId', next.employeeId)
        }
        return params
      })
    },
    [setSearchParams],
  )

  const showOverview = useCallback(() => setView({ mode: 'overview' }), [setView])
  const showAll = useCallback(() => setView({ mode: 'all' }), [setView])
  const showEmployee = useCallback(
    (employeeId: string) => setView({ mode: 'employee', employeeId }),
    [setView],
  )

  return { view, showOverview, showAll, showEmployee }
}
